<template>
	<div class="p4 xs-right">
		<div class="xs-12 mb4 fs3">
			<strong>{{ $t("to") }}: </strong>{{ createdBy.username }}
		</div>

		<!-- template -->
		<div class="xs-12 flex xs-between mb2 fs3 fw7">
			<div>{{ $t("notification_template") }}</div>
			<j-button type="tertiary" :to="{ name: 'TemplatesList' }">{{
				$t("manage_templates")
			}}</j-button>
		</div>
		<j-base-select
			:options="templates"
			name="template"
			v-model="selectedTemplate"
			v-validate="'required'"
			:status="errors && errors.first('template') ? 'error' : null"
		/>
		<!-- Error message -->
		<div
			class="xs-12 fs3 lh125 cError mt1"
			v-if="errors && errors.first('template')"
		>
			{{ errors ? errors.first("template") : null }}
		</div>

		<!-- content -->
		<div class="xs-12 mt4" v-if="parsedContent.length">
			<div class="fw7 fs3 mt2 mb2">{{ $t("content") }}</div>
			<div v-for="(item, i) in parsedContent" :key="i" class="mb2">
				<div class="fs3 mb1">{{ item.label }}</div>
				<j-base-input-text v-model="item.value" size="small" />
			</div>
		</div>

		<!-- Send -->
		<j-button
			class="mt4"
			:isLoading="isSendLoading"
			@click="sendNotification"
			>{{ $t("send_notification") }}</j-button
		>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JBaseSelect from "@/components/JBaseSelect"
import JBaseInputText from "@/components/JBaseInputText"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JButton,
		JBaseSelect,
		JBaseInputText
	},

	data() {
		return {
			templates: [],
			selectedTemplate: null,
			isSendLoading: false,
			contentJsonString: null,
			toEntityId: null,
			parsedContent: []
		}
	},

	computed: {
		createdBy() {
			return this.$store.state?.caseManagement?.case?.createdBy || {}
		},
		parsedContentToJsonString() {
			const entries = this.parsedContent.map(item => [item.label, item.value])
			return JSON.stringify(Object.fromEntries(entries))
		},
		dataJsonString() {
			return JSON.stringify({
				case: this.$store.state?.caseManagement?.case?.id
			})
		}
	},

	methods: {
		async sendNotification() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				mutation sendNotification($input: sendNotificationInput!) {
					sendNotification(input: $input) {
						status
						errors
					}
				}
			`

			const variables = {
				input: {
					toEntityId: this.toEntityId,
					internalMessage: {
						dataJsonString: this.dataJsonString,
						templateRendering: {
							templateId: this.selectedTemplate,
							input: {
								name: "data",
								contentJsonString: this.parsedContentToJsonString
							}
						}
					}
				}
			}

			this.isSendLoading = true
			const res = await api({ query, variables })
			this.isSendLoading = false

			if (
				res?.data?.errors?.length ||
				res.data?.sendNotification?.errors?.length
			) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.errors?.toString() ||
							res.data?.sendNotification?.errors
					}
				})
			}
			this.$emit("notification:sent")
		},

		async fetchTemplates() {
			const query = gql`
				query fetchTemplates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
							... on notificationTemplate {
								localizedHtml
							}
						}
					}
				}
			`

			const variables = {
				where: {
					type: "notification"
				}
			}

			const res = await api({ query, variables })

			this.templates = res?.data?.templates?.list?.map(item => ({
				name: item?.name,
				value: item?.id,
				localizedHtml: item.localizedHtml
			}))
		}
	},

	created() {
		this.fetchTemplates()
	},

	watch: {
		selectedTemplate: {
			handler(n) {
				if (n) {
					const html =
						this.templates?.find(t => t.value === n)?.localizedHtml || ""
					this.parsedContent =
						html
							.match(/[^{\}]+(?=})/g)
							?.map(item => item.trim())
							?.filter(item => item?.length)
							?.filter(item => item.startsWith("data."))
							?.map(item => {
								return {
									label: item.replace("data.", ""),
									value: null
								}
							}) || []
				} else {
					this.parsedContent = []
				}
			}
		},

		createdBy: {
			async handler(n) {
				if (n) {
					// Need to get internal id of the user
					const query = gql`
						query logins($ids: [String]) {
							logins(ids: $ids) {
								list {
									associatedUser {
										id
									}
								}
							}
						}
					`
					const res = await api({ query, variables: { ids: [n.id] } })
					this.toEntityId = res?.data?.logins?.list?.[0]?.associatedUser?.id
				}
			},
			deep: true,
			immediate: true
		}
	}
}
</script>
